import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Users, UserCog, UserX, Contact, Briefcase, Layers, Activity, Clock, UserCheck, TrendingUp, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const RANGES = [
  { key: "today", label: "Oggi" },
  { key: "7d", label: "7 giorni" },
  { key: "30d", label: "30 giorni" },
];
const nn = (v) => (v == null ? "–" : v);

function StatCard({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const toneCls = {
    slate: "text-slate-500", violet: "text-[#6D5FB8]", green: "text-emerald-600",
    orange: "text-orange-600", blue: "text-blue-600",
  }[tone];
  return (
    <Card className="rounded-xl border-slate-200 shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
          <Icon className={`h-4 w-4 ${toneCls}`} /> {label}
        </div>
        <p className="text-2xl font-heading font-bold text-slate-900 leading-tight">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-heading font-semibold text-slate-700 mb-2">{title}</h3>
      {children}
    </div>
  );
}

export function StatisticheSection() {
  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((rg) => {
    setLoading(true);
    api.get(`/admin/system/stats?range=${rg}`).then((r) => setData(r.data))
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(range); }, [range, load]);

  const a = data?.accounts || {};
  const org = data?.org || {};
  const u = data?.usage || {};
  const chartData = (data?.usage_series || []).map((p) => {
    const d = new Date(p.label.length <= 10 ? `${p.label}T00:00:00` : p.label);
    const lbl = range === "today"
      ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    return { label: lbl, interazioni: p.interazioni };
  });

  return (
    <div className="space-y-6" data-testid="statistiche-section">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">Statistiche</h2>
        <p className="text-sm text-slate-500">Dati complessivi di Orbit su tutti gli appalti e reparti.</p>
      </div>

      <Group title="Account">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard icon={Users} tone="violet" label="Lavoratori attivi" testid="st-lav" value={nn(a.lavoratori_attivi)} />
          <StatCard icon={UserCog} tone="blue" label="Ufficio attivi" testid="st-uff" value={nn(a.ufficio_attivi)} />
          <StatCard icon={UserX} tone="slate" label="Disabilitati" testid="st-disatt" value={nn(a.disattivati)} sub="Lavoratore/Ufficio non attivi" />
        </div>
      </Group>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Group title="Utenti gestiti">
          <StatCard icon={Contact} tone="green" label="Utenti/assistiti in anagrafica" testid="st-utenti" value={nn(data?.utenti_gestiti)} sub="distinti dagli account di accesso" />
        </Group>
        <Group title="Dimensione organizzativa">
          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={Briefcase} tone="orange" label="Appalti attivi" testid="st-appalti" value={nn(org.appalti_attivi)} />
            <StatCard icon={Layers} tone="violet" label="Servizi attivi" testid="st-servizi" value={nn(org.servizi_attivi)} />
          </div>
        </Group>
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-sm font-heading font-semibold text-slate-700">Utilizzo di Orbit</h3>
            <div className="flex gap-1.5 bg-slate-100 rounded-lg p-1" data-testid="st-range-selector">
              {RANGES.map((r) => (
                <button key={r.key} onClick={() => setRange(r.key)} data-testid={`st-range-${r.key}`}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${range === r.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="st-usage-cards">
            <StatCard icon={Activity} tone="violet" label="Interazioni significative" testid="st-interazioni" value={nn(u.interazioni)} />
            <StatCard icon={Clock} tone="orange" label="Timbrature" testid="st-timbrature" value={nn(u.timbrature)} />
            <StatCard icon={UserCheck} tone="blue" label="Account unici" testid="st-unici" value={nn(u.account_unici)} sub="Lavoratore/Ufficio" />
            <StatCard icon={TrendingUp} tone="green" label="Media interazioni/account" testid="st-media" value={nn(u.media_interazioni_account)} />
            <StatCard icon={Zap} tone="slate" label="Picco attivi (5 min)" testid="st-picco" value={nn(u.picco_attivi_5min)} />
          </div>

          <div className="h-64" data-testid="st-chart">
            {loading ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">Caricamento…</div>
            ) : chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="st-chart-empty">Nessuna interazione registrata in questo intervallo.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} minTickGap={20} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} width={40} allowDecimals={false} />
                  <Tooltip formatter={(v) => [`${v}`, "Interazioni"]}
                    contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="interazioni" fill="#6D5FB8" radius={[4, 4, 0, 0]} maxBarSize={38} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default StatisticheSection;
